import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import { dates } from '@omegagrid/core';
import { msg, getLocale } from '@omegagrid/localize';
import rp from '@reporting/rp-app-host';
import { AccountingRights } from '@reporting/rp-types/core';
import { EntityAccessType } from '@reporting/rp-types/users';
import { AgentSchedule } from '@reporting/rp-types/jobs';
import { style } from './agentWatcherInfo.style';

const formatDateTime = (value: string) => dates.getDateFormatter(getLocale(), dates.getDateFormat({ withTime: true }))(value);

type ActionButton = { icon: string, color: string, text: string, action: () => void };

@customElement('rp-app-agentwatcherinfo')
export class AgentWatcherInfo extends LitElement {

	static styles = [style];

	@property({type: Object})
	schedule: AgentSchedule;

	
	@property({type: Boolean})
	pending = false;

	get canPlan() {
		return rp.userProvider.hasRight([AccountingRights.JobsRead, AccountingRights.JobsDefine]);
	}

	/** ASAP once the run is requested - the customer's agent picks it up on its next check. */
	get nextRun() {
		if (this.schedule.run_now) return msg('ASAP');
		return this.schedule.next_run ? formatDateTime(this.schedule.next_run) : '-';
	}

	/** Stop while the run is requested (run_now), Start otherwise. */
	get actionButton(): ActionButton {
		return this.schedule.run_now
			? { icon: 'stop', color: 'red', text: msg('Stop'), action: () => this.stop() }
			: { icon: 'play', color: 'green', text: msg('Start'), action: () => this.start() };
	}

	start() {
		this.dispatchEvent(new CustomEvent('start'));
	}

	stop() {
		this.dispatchEvent(new CustomEvent('stop'));
	}

	_onAction(e: PointerEvent) {
		e.stopImmediatePropagation();
		this.actionButton.action();
	}

	openPlans(e: PointerEvent) {
		e.stopImmediatePropagation();
		rp.host.open(
			`jobs-agentPlans-${this.schedule.id_agent_schedule}`,
			{ title: `${msg('Plan')}: ${this.schedule.name}` }
		);
	}

	openAccess(e: PointerEvent) {
		e.stopImmediatePropagation();
		rp.host.open(
			`users-entityUsers-${EntityAccessType.Agent}-${this.schedule.id_agent_schedule}`,
			{ title: `${msg('Access')}: ${this.schedule.name}` }
		);
	}

	render = () => html`
		<og-panel collapsible collapsed>
			<div class="header" slot="header">
				<div class="agent-info">
					<og-icon class="agent-icon" icon="upload"></og-icon>
					<div class="agent-title">${msg(this.schedule.name)}</div>
				</div>
				<div>
					${when(this.schedule?.run_now, () => html`<og-loader icon="spinner"></og-loader>`)}
				</div>
			</div>
			<div class="body" slot="body">
				${when(this.schedule, () => html`
					<table>
						<tr>
							<td colspan="2">
								<div class="agent-buttons">
									${this.renderActionButton()}
									${when(this.canPlan, () => html`
										<og-button icon="calendar-days" .title="${msg('Plans')}" @click="${this.openPlans}"></og-button>
									`)}
									<og-button icon="shield-check" .title="${msg('Access')}" @click="${this.openAccess}"></og-button>
								</div>
							</td>
						</tr>
						<tr>
							<td>${msg('Next start')}</td>
							<td>${this.nextRun}</td>
						</tr>
					</table>
				`)}
			</div>
		</og-panel>
	`;

	renderActionButton() {
		const button = this.actionButton;
		return html`
			<og-button
				class="action"
				icon="${button.icon}"
				color="${button.color}"
				.text="${button.text}"
				?disabled="${this.pending}"
				@click="${this._onAction}">
			</og-button>
		`;
	}

}
